import { getScale, type ScaleInfo } from "./scales";
import { nameToPitchClass, pitchClass, pitchClassName, type Accidentals } from "./notes";

export type Mode = "major" | "minor";

export interface MusicalKey {
  tonic: string; // spelled, e.g. "G", "F#", "Bb"
  mode: Mode;
}

/** Conventional key spellings by pitch class (fewest accidentals; F#/Gb → F# major, Eb minor). */
const MAJOR_TONICS = ["C", "Db", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"] as const;
const MINOR_TONICS = ["C", "C#", "D", "Eb", "E", "F", "F#", "G", "G#", "A", "Bb", "B"] as const;

export function keyTonics(mode: Mode): readonly string[] {
  return mode === "major" ? MAJOR_TONICS : MINOR_TONICS;
}

export function keyName(key: MusicalKey): string {
  return `${key.tonic} ${key.mode}`;
}

export function keyTonicPc(key: MusicalKey): number {
  const pc = nameToPitchClass(key.tonic);
  if (pc === null) throw new Error(`Invalid key tonic: ${key.tonic}`);
  return pc;
}

export function isValidKey(key: MusicalKey): boolean {
  return (keyTonics(key.mode) as readonly string[]).includes(key.tonic);
}

export function keyScale(key: MusicalKey): ScaleInfo {
  return getScale(key.tonic, key.mode === "major" ? "major" : "minor");
}

/** Transpose a key by semitones, choosing the conventional spelling. */
export function transposeKey(key: MusicalKey, semitones: number): MusicalKey {
  const pc = pitchClass(keyTonicPc(key) + semitones);
  return { tonic: keyTonics(key.mode)[pc]!, mode: key.mode };
}

/** Does this key's signature use flats? (C major / A minor → sharps by convention.) */
export function keyAccidentals(key: MusicalKey): Accidentals {
  return keyScale(key).notes.some((n) => n.includes("b")) ? "flats" : "sharps";
}

/** Number of sharps (positive) or flats (negative) in the key signature. */
export function keySignature(key: MusicalKey): number {
  const notes = keyScale(key).notes;
  const sharps = notes.filter((n) => n.includes("#")).length;
  const flats = notes.filter((n) => n.includes("b")).length;
  if (sharps > 0) return sharps;
  return flats > 0 ? -flats : 0; // avoid -0 for C major / A minor
}

/** Spell a pitch class in the context of a key: scale spelling if diatonic. */
export function spellInKey(pc: number, key: MusicalKey): string {
  const scale = keyScale(key);
  const idx = scale.pitchClasses.indexOf(pitchClass(pc));
  return idx >= 0 ? scale.notes[idx]! : pitchClassName(pc, keyAccidentals(key));
}

export function relativeKey(key: MusicalKey): MusicalKey {
  return key.mode === "major" ? transposeKey({ ...key, mode: "minor" }, -3) : transposeKey({ ...key, mode: "major" }, 3);
}

/** Semitone offset between two keys in the range -6..+6 (shortest move, prefers up for tritone). */
export function semitonesBetween(from: MusicalKey, to: MusicalKey): number {
  let d = pitchClass(keyTonicPc(to) - keyTonicPc(from));
  if (d > 6) d -= 12;
  return d;
}
